import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { formatDateTime } from '@/lib/format';
import { Badge, EmptyState } from '@/components/ui';
import { ListSkeleton, PageHeader, Section } from './page-parts';

/**
 * `/:locale/live` — every live event, running ones first (the API orders them).
 *
 * The timeline itself is Phase 11, over SSE; this is the index that gets a reader into
 * it. Never cached (§1.11), which is why the query treats its data as immediately stale.
 */
export default function LivePage() {
  const { locale, t } = useLocale();

  const { data, isPending } = useQuery({
    queryKey: queryKeys.liveEvents(locale),
    queryFn: () => publicApi.liveEvents(locale),
    staleTime: STALE.realtime,
  });

  return (
    <>
      <PageHeader title={t('section.liveUpdates')} />
      <Section>
        {isPending ? (
          <ListSkeleton rows={4} label={t('status.loading')} />
        ) : (data?.length ?? 0) === 0 ? (
          <EmptyState title={t('live.none')} />
        ) : (
          <ul className="flex flex-col gap-3">
            {(data ?? []).map((event) => (
              <li key={event.id}>
                <Link
                  to={event.path}
                  className="group flex flex-col gap-2 rounded-lg border border-line bg-white p-4 transition hover:border-red"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    {event.status === 'live' ? (
                      <Badge tone="live">{t('live.now')}</Badge>
                    ) : (
                      <Badge tone="muted">{formatDateTime(event.startTime, locale)}</Badge>
                    )}
                    <span className="text-xs text-muted">
                      {t('live.updateCount', { count: event.updateCount })}
                    </span>
                  </span>
                  <span className="text-xl font-extrabold text-ink transition group-hover:text-red">
                    {event.title}
                  </span>
                  {event.description && (
                    <span className="text-base text-body">{event.description}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
